import { startCase } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { t } from '@lib/translation/helpers';
import { SnCamera } from '@lib/api/entities';
import { AiAPI } from '@lib/api/services';
import { PursuitService } from '@lib/cameras/services/pursuit.service';

@Component({
  selector: 'fs-snap-ai-radar',
  templateUrl: './snap-ai-radar.component.html',
  styleUrls: ['./snap-ai-radar.component.scss'],
})
export class SnapAiRadarComponent implements OnInit, OnDestroy {
  @Input()
  set inputData(value: any) {
    this.newData = value;
    if (this.resultsData) {
      this.resultsData = null;
      this.response = null;
      this._cd.detectChanges();
      this.createArray();
    }
  }

  @Output()
  public readonly newCam = new EventEmitter<any>();

  get createSentry$(): Observable<any[]> {
    return this._createSentry$.asObservable();
  }

  public form = new UntypedFormGroup({});
  public resultsData: any = null;
  public response: any;
  public dataUrl = '';
  public item: any;
  public isLoading: boolean;
  public newData: SnCamera[];
  public activeSentry: any;

  public formFields = [
    {
      key: 'file',
      type: 'file-upload',
      label: t('map.snapAiSelector.uploadPhoto'),
      templateOptions: {
        accept: '.jpg, .jpeg, .png',
        maxFileSize: 10000000,
        multiple: false,
        change: (fieldValue, fileValue) => {
          if (!fileValue || !fileValue[0]) {
            return;
          }
          this.isLoading = true;
          const formData = new FormData();
          formData.append('file', fileValue[0], fileValue[0].name);
          this.dataUrl = URL.createObjectURL(fileValue[0]);
          this._getSnapAi(formData)
            .pipe(takeUntil(this._destroy$))
            .subscribe((value) => {
              this.response = value;
              this.isLoading = false;
              this._cd.detectChanges();
            });
        },
      },
    },
  ];

  private readonly _destroy$ = new Subject();

  private readonly _createSentry$ = new Subject<string[]>();

  constructor(
    private readonly _aiAPI: AiAPI,
    private readonly _sanitizer: DomSanitizer,
    private readonly _cd: ChangeDetectorRef,
    public readonly _pursuitAPI: PursuitService,
  ) {}

  public getSnapAi(form: FormData): void {
    this._getSnapAi(form)
      .pipe(takeUntil(this._destroy$))
      .subscribe((value) => {
        this.response = value;
        this.isLoading = false;
        this._cd.detectChanges();
      });
  }

  public ngOnInit(): void {
    this._pursuitAPI
      .getNewCam()
      .pipe(takeUntil(this._destroy$))
      .subscribe((camObj: any) => {
        this.newCam.emit(camObj);
        this._cd.detectChanges();
      });

    this._pursuitAPI
      .getSnapAi()
      .pipe(takeUntil(this._destroy$))
      .subscribe((snap: any) => {
        this.isLoading = true;
        const formData = new FormData();
        formData.append('file', snap.file, snap.file.name);
        this.dataUrl = URL.createObjectURL(snap.file);
        this.getSnapAi(formData);
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public transform(label: string): string {
    return startCase(label);
  }

  public clearResults(): void {
    this.resultsData = [];
  }

  public screenshot(): void {
    this._pursuitAPI.setScreenshot(true);
  }

  public sanitizeImg(url: string): SafeUrl {
    return this._sanitizer.bypassSecurityTrustUrl(url);
  }

  public previewItem(item: any): void {
    this.item = item;
    this._cd.detectChanges();
  }

  public createArray(): any {
    const cameraid = [];
    this.newData.forEach((element) => {
      if (element.isAiCamera) {
        cameraid.push(element.id);
      }
    });
    this.viewResults(cameraid);
  }

  public viewResults(cameraList: string[]): void {
    const data: any = {
      aiCameras: cameraList,
      label: this.item.label,
      confidence: this.item.confidence - 20,
    };
    if (this.item.label === 'person') {
      this._pursuitAPI.setDetectionType('person');
      this.item.metadata.attributes.forEach((item) => {
        data[item.label] = item.confidence > 50 ? '>=50' : '< 50';
      });

      if (this.item.metadata.colorBottom?.label) {
        data.colorBottom = this.item.metadata.colorBottom?.label;
      }

      if (this.item.metadata.colorTop?.label) {
        data.colorTop = this.item.metadata.colorTop?.label;
      }
    }

    if (this.item.label === 'car') {
      data.vehicleType = this.item.metadata.vehicleType[0].label;
      data.carColors = this.item.metadata.carColor[0].label;
      data.colorConfidence = this.item.metadata.carColor[0].confidence;
    }

    if (
      this.item.label === 'car' ||
      this.item.label === 'truck' ||
      this.item.label === 'bus' ||
      this.item.label === 'bicycle'
    ) {
      this._pursuitAPI.setDetectionType('car');
    }

    const queryParams = this._formToParams(data);
    this.resultsData = queryParams;
    this._cd.detectChanges();
  }

  public removeImage(): void {
    this.response = null;
    this.item = null;
    this.activeSentry = null;
    this.resultsData = null;
    this.isLoading = false;
  }

  private _formToParams(formData: any): any {
    const cameras = formData.aiCameras;
    const date = new Date();
    const offset = (date.getTimezoneOffset() * -1) / 60;
    const startTime = `${0 / 60}, ${offset}`;
    const endTime = `${1440 / 60}, ${offset}`;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 1);
    const endDate = new Date();
    const params: any = {
      camera_id__in: cameras.join(','),
      label__in: formData.label,
      timestamp__gte: Math.floor(startDate.getTime() / 1000),
      timestamp__lte: Math.ceil(endDate.getTime() / 1000),
      timestamp__hour__gte__offset: startTime,
      timestamp__hour__lt__offset: endTime,
      confidence__gte: formData.confidence,
    };

    switch (formData.label) {
      case 'motion':
      case 'camera-motion':
        params.confidence__gte = 0;
        break;

      case 'car':
        params.metadata__vehicle_type__in = formData.vehicleType;
        params.metadata__car_color__in = formData.carColors;
        params.metadata__car_color__confidence__gte = formData.colorConfidence;
        break;
      case 'person':
        if (formData.is_male === '>=50') {
          params.metadata__attributes__is_male__confidence__gte = 50;
        } else if (formData.is_male === '< 50') {
          params.metadata__attributes__is_male__confidence__lte = 50;
        }
        if (formData.has_hat === '>=50') {
          params.metadata__attributes__has_hat__confidence__gte = 50;
        } else if (formData.has_hat === '< 50') {
          params.metadata__attributes__has_hat__confidence__lte = 50;
        }
        if (formData.has_backpack === '>=50') {
          params.metadata__attributes__has_backpack__confidence__gte = 50;
        } else if (formData.has_backpack === '< 50') {
          params.metadata__attributes__has_backpack__confidence__lte = 50;
        }
        if (formData.has_bag === '>=50') {
          params.metadata__attributes__has_bag__confidence__gte = 50;
        } else if (formData.has_bag === '< 50') {
          params.metadata__attributes__has_bag__confidence__lte = 50;
        }
        if (formData.colorTop) {
          params.metadata__color_top__in = formData.colorTop;
        }

        if (formData.colorBottom) {
          params.metadata__color_bottom__in = formData.colorBottom;
        }
        break;
    }
    return params;
  }

  private _getSnapAi(file: any): Observable<any> {
    return this._aiAPI.snapAi(file);
  }
}
